import { addErrorNotice, catchWithAuthErrors } from 'Data/Helpers/rxjsHelpers';
import { RootState } from 'Data/Redux';
import { Epic, StateObservable, ofType } from 'redux-observable';
import { of } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';
import { actions } from '.';
import { syncAndAwaitResolve } from '../DataSync';
import {
  callApiCreateAssignedValueTest,
  callApiGetAvailableMddTests,
  callApiGetSourceEntities,
  callApiPostSelectedTestResults,
} from './apiCalls';

import {
  CreateAssignedValueTestRequest,
  GetAvailableMaximumDryDensityTestsAction,
  GetSourceEntitiesAction,
  SelectedTestResults,
} from './types';

/**
 * Respond to a getAvailableMdds action by validating the current query
 * and, if valid, calling the api to fetch the worksheets
 */
export const getAvailableMdds: Epic = (action$) => {
  return action$.pipe(
    ofType(actions.getAvailableMdds.toString()),
    switchMap((action: GetAvailableMaximumDryDensityTestsAction) => {
      let query = action?.payload.query;
      return query
        ? callApiGetAvailableMddTests(query).pipe(
            switchMap((response) => of(actions.getAvailableMddsResolve(response))),
            catchWithAuthErrors((err: any) =>
              of(actions.getAvailableMddsReject(), addErrorNotice('Failed to retrieve worksheets.', err))
            )
          )
        : of(actions.getAvailableMddsResolve({ Worksheets: [], Messages: [] }));
    })
  );
};

/**
 * Creates an Assigned Value Test from the given parameters
 */
export const createAssignedValueTest: Epic = (action$, state$: StateObservable<RootState>) => {
  return action$.pipe(
    ofType(actions.createAssignedValueTest.toString()),
    switchMap(() => {
      //Build up request
      var selectedTests = getSelectedTests(state$);
      const request = {
        AssignedMaximumDryDensity: state$.value.assignedMaximumDryDensityTests?.results?.MeanMaximumDryDensity!,
        AssignedOptimumMoistureContent:
          state$.value.assignedMaximumDryDensityTests?.results?.MeanOptimumMoistureContent!,
        DateAssigned: state$.value.assignedMaximumDryDensityTests?.assignedDate!,
        Tests: selectedTests,
      } as CreateAssignedValueTestRequest;

      return request
        ? callApiCreateAssignedValueTest(state$.value.worksheetEditor.worksheetId!, request).pipe(
            switchMap(() => syncAndAwaitResolve(action$, of(actions.createAssignedValueTestResolve()))),
            catchWithAuthErrors((err: any) =>
              of(actions.createAssignedValueTestReject(), addErrorNotice('Failed to retrieve worksheets.', err))
            )
          )
        : of(actions.createAssignedValueTestResolve());
    })
  );
};

/**
 * Helper function Generate the query from selected tests.
 * Tests are retrieved via state
 */
const createSelectedTestResultsQuery = (state$: StateObservable<RootState>) => {
  return {
    Tests: state$.value.assignedMaximumDryDensityTests.mddTests
      ?.Worksheets!.filter((row) => row.selected)
      .map(
        (row) =>
          ({
            MaximumDryDensity: row?.maximumDryDensity?.value,
            OptimumMoistureContent: row?.optimumMoistureContent,
          }) as SelectedTestResults
      ),
  };
};

const getSelectedTests = (state$: StateObservable<RootState>) => {
  return state$.value.assignedMaximumDryDensityTests?.mddTests?.Worksheets.filter((row) => row.selected);
};

/**
 * Post an array of selected tests to calculate interim results
 */
export const postSelectedTestResults: Epic = (action$, state$: StateObservable<RootState>) => {
  return action$.pipe(
    ofType(actions.postSelectedTestsResults.toString()),
    switchMap(() => {
      let query = createSelectedTestResultsQuery(state$);
      return query
        ? callApiPostSelectedTestResults(query).pipe(
            switchMap((results) => of(actions.postSelectedTestsResultsResolve(results))),
            catchWithAuthErrors((err: any) =>
              of(actions.postSelectedTestsResultsReject(), addErrorNotice('Failed to retrieve results.', err))
            )
          )
        : of(
            actions.postSelectedTestsResultsResolve({
              messages: [],
              results: {
                GreatestDifferenceFromMeanMdd: undefined,
                GreatestDifferenceFromMeanOmc: undefined,
                MeanMaximumDryDensity: undefined,
                MeanOptimumMoistureContent: undefined,
              },
            })
          );
    })
  );
};

/**
 * Respond to a getSourceEntities action by validating the current query
 * and, if valid, calling the api to fetch the entities
 */
export const getSourceEntities: Epic = (action$, state$: StateObservable<RootState>) => {
  return action$.pipe(
    ofType(actions.getSourceEntities.toString()),
    switchMap((action: GetSourceEntitiesAction) => {
      let request = action?.payload.request;
      return request
        ? callApiGetSourceEntities(request).pipe(
            mergeMap((response) =>
              request?.reimport
                ? syncAndAwaitResolve(action$, of(actions.getSourceEntitiesResolve(response.Worksheets)))
                : of(actions.getSourceEntitiesResolve(response.Worksheets))
            ),
            catchWithAuthErrors((err: any) =>
              of(actions.getSourceEntitiesReject(), addErrorNotice('Failed to retrieve worksheets.', err))
            )
          )
        : of(actions.getSourceEntitiesResolve({ entities: [] }));
    })
  );
};
